import Constants from "constants/Constants";
import { startCase } from "helpers/string";
import { resolvePermissionCategory } from "routes/admin/components/permissionCategory";

export const METHOD_LABELS = {
  post: "Create",
  get: "Read",
  put: "Update",
  delete: "Delete",
};

export const formatCategory = category =>
  startCase(category.replace(/_/g, " "));

// Swagger path parameters are swapped for sample values so the category and
// role-permission regexes can be tested against a concrete path.
const toSamplePath = path =>
  path.replace(/{\w+_id}/gi, 1).replace(/{\w+}/gi, "str");

const toPathPattern = path =>
  "^" +
  path
    .replace(/{\w+_id}/gi, "\\d+")
    .replace(/{\w+}/gi, "\\w+")
    .replace(/\/$/, "") +
  "/?$";

/**
 * Groups every swagger endpoint by permission category, flagging the ones the
 * role already allows. A role permission matches an endpoint either through
 * its path regex or through an `@category` rule covering the whole category.
 */
export const buildCategoryPermissions = (
  swaggerPaths,
  permissionCategories,
  rolePermissions = []
) =>
  Object.entries(swaggerPaths).reduce((byCategory, [path, pathMethods]) => {
    const samplePath = toSamplePath(path);
    const category = resolvePermissionCategory(
      samplePath,
      permissionCategories
    );
    if (!category) return byCategory;

    const endpoints = Object.entries(pathMethods)
      .filter(([method]) => Constants.HTTP_METHODS.includes(method))
      .map(([method, { operationId: description }]) => ({
        path,
        method,
        description,
        is_allowed: rolePermissions.some(
          permission =>
            (samplePath.match(RegExp(permission.path)) ||
              category === permission.path.replace("@", "")) &&
            permission.method === method
        ),
      }));

    byCategory[category] = [...(byCategory[category] || []), ...endpoints];
    return byCategory;
  }, {});

export const getAllowedCategories = categoryPermissions =>
  Object.entries(categoryPermissions)
    .filter(([, endpoints]) => endpoints.some(p => p.is_allowed))
    .map(([category]) => category);

/**
 * Builds the payload the role endpoints expect. A method allowed on every
 * endpoint of a category collapses into one `@category` rule, which also
 * covers endpoints added to that category later; otherwise each allowed
 * endpoint becomes its own anchored path regex.
 */
export const serializePermissions = categoryPermissions =>
  Object.entries(categoryPermissions).flatMap(([category, endpoints]) =>
    Object.keys(METHOD_LABELS).flatMap(method => {
      const methodEndpoints = endpoints.filter(p => p.method === method);
      const isAllAllowed =
        methodEndpoints.length > 0 && methodEndpoints.every(p => p.is_allowed);
      if (isAllAllowed) {
        return [{ method, path: `@${category}`, is_allowed: true }];
      }
      return methodEndpoints
        .filter(p => p.is_allowed)
        .map(({ path, is_allowed }) => ({
          method,
          path: toPathPattern(path),
          is_allowed,
        }));
    })
  );
